/**
 * Notes client hooks (B6).
 *
 * Note objects via /api/notes: pin to a deal, capture globally, mark done,
 * delete, convert, and delegate. Thin wrappers; any list invalidation refreshes
 * every notes query (partial key match on ['notes','list']).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, queryKeys } from './api.js';

export interface Note {
  id: string;
  body: string;
  pinned: boolean;
  status: 'open' | 'done' | 'archived';
  dealThreadId: string | null;
  partyId: string | null;
  messageId: string | null;
  documentId: string | null;
  authorUserId: string | null;
  assignedToUserId: string | null;
  delegatedToNeo: boolean;
  delegatedToNeoCode: boolean;
  convertedTaskId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NoteHistoryEntry {
  id: string;
  action: string;
  userId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export type NotesScope = 'mine' | 'all';

export function useNotes(
  params: { dealThreadId?: string; status?: string; pinned?: boolean; scope?: NotesScope } = {},
  enabled = true,
) {
  const qs = new URLSearchParams();
  if (params.dealThreadId) qs.set('dealThreadId', params.dealThreadId);
  if (params.status) qs.set('status', params.status);
  if (params.pinned) qs.set('pinned', 'true');
  if (params.scope) qs.set('scope', params.scope);
  const query = qs.toString();
  return useQuery({
    queryKey: queryKeys.notes.list(params),
    queryFn: () => api.get<{ notes: Note[] }>(`/api/notes${query ? `?${query}` : ''}`),
    enabled,
  });
}

export function useNoteHistory(noteId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['notes', 'history', noteId],
    queryFn: () => api.get<{ entries: NoteHistoryEntry[] }>(
      `/api/audit?entityType=note&limit=25&entityId=${encodeURIComponent(noteId ?? '')}`
    ),
    enabled: enabled && Boolean(noteId),
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; dealThreadId?: string | null; pinned?: boolean }) =>
      api.post<{ note: Note }>('/api/notes', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', 'list'] }),
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      body?: string;
      pinned?: boolean;
      status?: 'open' | 'done' | 'archived';
      assignedToUserId?: string | null;
      delegatedToNeo?: boolean;
      delegatedToNeoCode?: boolean;
    }) => api.patch<{ note: Note }>(`/api/notes/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', 'list'] }),
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/notes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', 'list'] }),
  });
}

export function useConvertNoteToTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ note: Note; taskId: string }>(`/api/notes/${id}/convert-to-task`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', 'list'] }),
  });
}
